// Generated by ReScript, PLEASE EDIT WITH CARE
'use strict';

let React = require("react");
let Stdlib_Array = require("@rescript/runtime/lib/js/Stdlib_Array.js");
let Primitive_option = require("@rescript/runtime/lib/js/Primitive_option.js");

function composeUpdaters(updaters) {
  return draft => Stdlib_Array.reduce(updaters, draft, (draft, updater) => updater(draft));
}

function useDebouncedSave(saveNow, waitMsOpt) {
  let waitMs = waitMsOpt !== undefined ? waitMsOpt : 750;
  let saveNowRef = React.useRef(saveNow);
  let saveDisposable = React.useRef(undefined);
  let saveTimeout = React.useRef(undefined);
  let pendingDraft = React.useRef(undefined);
  saveNowRef.current = saveNow;
  let clearSaveDisposable = () => {
    let disposable = saveDisposable.current;
    if (disposable !== undefined) {
      Primitive_option.valFromOption(disposable).dispose();
      saveDisposable.current = undefined;
      return;
    }
  };
  let clearSaveTimeout = () => {
    let timeoutId = saveTimeout.current;
    if (timeoutId !== undefined) {
      clearTimeout(Primitive_option.valFromOption(timeoutId));
      saveTimeout.current = undefined;
      return;
    }
  };
  let runPendingSave = () => {
    let draft = pendingDraft.current;
    if (draft !== undefined) {
      pendingDraft.current = undefined;
      saveTimeout.current = undefined;
      saveDisposable.current = Primitive_option.some(saveNowRef.current(Primitive_option.valFromOption(draft)));
      return;
    }
  };
  React.useEffect(() => (() => {
    let timeoutId = saveTimeout.current;
    if (timeoutId !== undefined) {
      clearTimeout(Primitive_option.valFromOption(timeoutId));
      return runPendingSave();
    }
  }), []);
  return draft => {
    clearSaveDisposable();
    clearSaveTimeout();
    pendingDraft.current = Primitive_option.some(draft);
    saveTimeout.current = Primitive_option.some(setTimeout(() => runPendingSave(), waitMs));
  };
}

function useSequentialDebouncedSave(saveNow, waitMsOpt) {
  let waitMs = waitMsOpt !== undefined ? waitMsOpt : 750;
  let saveNowRef = React.useRef(saveNow);
  let pendingDraft = React.useRef(undefined);
  let saveState = React.useRef("Ready");
  saveNowRef.current = saveNow;
  let savePendingDraft = () => {
    let draft = pendingDraft.current;
    if (draft === undefined) {
      saveState.current = "Ready";
      return;
    }
    pendingDraft.current = undefined;
    let disposable = saveNowRef.current(Primitive_option.valFromOption(draft), () => {
      saveState.current = "Ready";
      savePendingDraft();
    });
    saveState.current = {
      TAG: "Saving",
      _0: disposable
    };
  };
  let scheduleSave = () => {
    saveState.current = {
      TAG: "Debouncing",
      _0: setTimeout(() => {
        saveState.current = "Ready";
        savePendingDraft();
      }, waitMs)
    };
  };
  React.useEffect(() => (() => {
    let timeoutId = saveState.current;
    if (typeof timeoutId !== "object") {
      return;
    }
    if (timeoutId.TAG === "Debouncing") {
      clearTimeout(timeoutId._0);
      return savePendingDraft();
    }
    timeoutId._0.dispose();
  }), []);
  return draft => {
    pendingDraft.current = Primitive_option.some(draft);
    let timeoutId = saveState.current;
    if (typeof timeoutId !== "object") {
      return scheduleSave();
    }
    if (timeoutId.TAG !== "Debouncing") {
      return;
    }
    clearTimeout(timeoutId._0);
    scheduleSave();
  };
}

function useScheduleUpdate(draft, updatedAt, setDraft, saveDraft) {
  let mostRecentDraftData = React.useRef(draft);
  mostRecentDraftData.current = draft;
  let match = React.useState(() => []);
  let setPendingUpdates = match[1];
  let pendingUpdates = match[0];
  let match$1 = React.useState(() => false);
  let setIsWaitingForPropagation = match$1[1];
  let isWaitingForPropagation = match$1[0];
  let match$2 = React.useTransition();
  let startApplyLocalDraftUpdatesTransition = match$2[1];
  React.useEffect(() => {
    if (pendingUpdates.length !== 0 && !isWaitingForPropagation) {
      let updater = composeUpdaters(pendingUpdates);
      let currentDraft = mostRecentDraftData.current;
      let newDraft = currentDraft !== undefined ? Primitive_option.some(updater(Primitive_option.valFromOption(currentDraft))) : undefined;
      setIsWaitingForPropagation(param => true);
      setPendingUpdates(u => u.filter(u => pendingUpdates.every(u_ => u !== u_)));
      startApplyLocalDraftUpdatesTransition(() => setDraft(newDraft));
      if (newDraft !== undefined) {
        saveDraft(Primitive_option.valFromOption(newDraft));
      }
    }
  }, [
    pendingUpdates,
    isWaitingForPropagation
  ]);
  React.useEffect(() => {
    setIsWaitingForPropagation(param => false);
  }, [updatedAt]);
  return updater => setPendingUpdates(pendingUpdates => pendingUpdates.concat([updater]));
}

exports.composeUpdaters = composeUpdaters;
exports.useDebouncedSave = useDebouncedSave;
exports.useSequentialDebouncedSave = useSequentialDebouncedSave;
exports.useScheduleUpdate = useScheduleUpdate;
/* react Not a pure module */
